/**
 * ONE place that answers "which vitrine surfaces are actionable at this onboarding stage".
 *
 * Both the router guard and the tab bar consume it. Two hand-maintained copies of the same rule is exactly the
 * split-brain this whole feature removes — and they had already drifted (the guard let `topup` through at
 * OPERATOR_ISSUE while the tab bar hid it).
 */
import type { OnboardingAction, OnboardingSnapshot, OnboardingStage } from '@/api/vitrineCard';

/**
 * Stages at which a client may open the payment screen: paying for the card IS a transfer.
 *
 * KYC and FUNDED_AWAITING_KYC belong here because in v2 the KYC track runs in PARALLEL with the first
 * top-up, not before it (PRIVATE_BANK_ONBOARDING_V2.md §A: "KYC и активация — внутри первого пополнения").
 * The server already agrees — the hard KYC gate on top-up submit was removed — so leaving these stages out
 * was the one thing still forcing the old sequential order: the guard bounced a client on the KYC track back
 * to /card, and the only way onwards was to finish KYC first.
 */
export const PAYMENT_STAGES: ReadonlyArray<OnboardingStage> =
  ['PAYMENT', 'PAYMENT_PENDING', 'KYC', 'FUNDED_AWAITING_KYC'];

/** Card-only surfaces need an actual card. */
export const CARD_STAGES: ReadonlyArray<OnboardingStage> = ['ISSUED'];

export const VITRINE_ROUTE_STAGES: Record<string, ReadonlyArray<OnboardingStage>> = {
  history: CARD_STAGES,
  topup: [...PAYMENT_STAGES, ...CARD_STAGES],
  // The master account's top-up (04 §8): from the home hero, which exists only with a card, and from /topup.
  'master-topup': [...PAYMENT_STAGES, ...CARD_STAGES],
  // A concierge task needs an issued card (REQ-003); the server refuses a card-less client anyway. `payments` is only a
  // redirect to the concierge tab now, and the tab itself is open at every stage (02 §1) — neither is in this map.
  'concierge-request': CARD_STAGES,
};

/**
 * Какие вкладки показывать в нижнем баре. НЕ то же самое, что «куда можно зайти».
 *
 * Маршрут `topup` открыт уже на оплате — иначе клиенту некуда нажать «Перейти к оплате», способы
 * оплаты ведут именно туда. Но вкладка на этой стадии вводила в заблуждение: карты ещё нет, переводить
 * нечего, а нижний бар обещает целый раздел — при том что нужное действие уже стоит кнопкой на самом
 * экране. Получались два входа в одно действие и ложное обещание.
 *
 * Поэтому вкладка появляется вместе с картой, а маршрут остаётся доступным раньше. Оба набора лежат
 * здесь рядом сознательно: разъехавшиеся копии одного правила — ровно то, от чего этот файл и заводился.
 */
export const VITRINE_TAB_STAGES: Record<string, ReadonlyArray<OnboardingStage>> = {
  history: CARD_STAGES,
  topup: CARD_STAGES,
};

/** Показывать ли вкладку на этой стадии. Неизвестная стадия — показываем: гейт всё равно подстрахует. */
export function tabAllowed(routeName: string, stage: OnboardingStage | null): boolean {
  const allowed = VITRINE_TAB_STAGES[routeName];
  if (!allowed || stage === null) return true;
  return allowed.includes(stage);
}

/** Is this route reachable at `stage`? An unknown stage is permissive — a guard must never strand a client. */
export function routeAllowed(routeName: string, stage: OnboardingStage | null): boolean {
  const allowed = VITRINE_ROUTE_STAGES[routeName];
  if (!allowed || stage === null) return true;
  return allowed.includes(stage);
}

/**
 * Does the client still owe us the issuance payment? Payment is required AND no payment exists yet.
 *
 * <p>The same question was being answered by three screens with three different formulas, and two of them
 * forgot the second half — so a client who had already paid was invited to pay again, on the documents screen
 * and on the top-up screen alike. One rule, one place.
 *
 * <p>NOT to be used for the offer screen's price: there the action is still ACCEPT_OFFER, the client could not
 * possibly have paid, and the decision to show the price has to be made before any payment can exist.
 */
export function owesIssuancePayment(snapshot: OnboardingSnapshot | null): boolean {
  return snapshot?.paymentRequired !== false && !snapshot?.payment;
}

/**
 * May we offer to create a NEW top-up request?
 *
 * Two cases, and only these: the card is already issued (topping up is the normal action then), or the client
 * has sent nothing yet. Everything in between means money is on its way or already with us while there is
 * still no card — offering a new request there is an invitation to pay twice while the first payment hangs.
 *
 * The "money in the system" signal is the snapshot's payment, the same one the card screen uses to decide
 * whether to show the price and the payment methods. One signal for both screens, or they drift apart.
 */
export function canCreateTopUp(
  nextAction: OnboardingAction | null,
  hasPayment: boolean,
): boolean {
  if (nextAction === 'OPEN_CARD') return true;
  return !hasPayment;
}

/**
 * Statuses that mean "the money is still in play or already ours" — the mirror of the server's
 * {@code TopUpStatus.LIVE_OR_FUNDED} ({@code ACTIVE ∪ COMPLETED}).
 *
 * <p>A CANCELLED / EXPIRED / REJECTED request is dead money: it collected nothing and hangs nowhere. Counting
 * it as "a payment exists" is what left a client who cancelled an unpaid request staring at "your payment is
 * on its way" with no way to start another — while the server happily accepted a new one. The two sides must
 * answer this question the same way, so this list is deliberately a copy of theirs, not a fresh guess.
 */
const PAYMENT_IN_PLAY = [
  'SUBMITTED', 'SCREENING', 'NEEDS_INFO', 'APPROVED', 'EXPORTED', 'IN_PROGRESS',
  'FUNDS_RECEIVED', 'COMPLETED',
];

/** Does this snapshot payment still count as money in the system? Unknown status ⇒ yes (fail-closed). */
export function paymentInPlay(status: string | null | undefined): boolean {
  if (!status) return false;
  return PAYMENT_IN_PLAY.includes(status.toUpperCase());
}

/**
 * Where to send the client once the action is done and there is nothing left to do on the current screen.
 * Null — stay put: yanking a client who still has something to do here is worse than not moving them.
 */
export const NEXT_SCREEN: Record<OnboardingAction, { name: string } | null> = {
  UPLOAD_DOCUMENTS: { name: 'card-issuance' },
  REVIEW_REQUESTED_DOCUMENTS: { name: 'card-issuance' },
  OPEN_CARD: { name: 'card' },
  WAIT_OPERATOR: { name: 'card' },          // "the manager is issuing your card" lives on /card
  WAIT_ISSUE_NO_PAYMENT: { name: 'card' },
  CONTACT_SUPPORT: { name: 'support' },
  WAIT_KYC: { name: 'card' },
  PAY_CARD: null,                            // still owes the payment — leave them on the top-up screen
  PAY_REST: null,                            // still owes the rest of it — same
  SEND_MONEY: null,
  WAIT_PAYMENT: null,
  ACCEPT_OFFER: null,
};
